import {
    Table, TableHead, TableBody, TableRow, TableCell, Tooltip,
} from '@mui/material';
import { COLUMN_IDS } from '../../../gibbs/question-chooser';

const GLYPH_CLEAN = '🟢';
const GLYPH_WARNING = '🟡';
const GLYPH_SEVERE = '🔴';
const GLYPH_UNAVAILABLE = '?';

function flagGlyph(flagsField) {
    if (flagsField === null || flagsField === undefined) return GLYPH_UNAVAILABLE;
    if (!Array.isArray(flagsField) || flagsField.length === 0) return GLYPH_CLEAN;

    let hasSevere = false;
    for (const f of flagsField) {
        const sev = typeof f === 'string' ? 'warning' : f && f.severity;
        if (sev === 'severe') {
            hasSevere = true;
            break;
        }
    }
    return hasSevere ? GLYPH_SEVERE : GLYPH_WARNING;
}

function formatScore(score) {
    if (score === null || score === undefined || !Number.isFinite(score)) return '—';
    return score.toFixed(1);
}

// Column renderers. Each entry returns the header cell and a body-cell
// builder. Driven by gibbs-question-chooser's frame emphasis — see
// frameEmphasis.js. The Rank column is always first; the rest reorder
// per the active frame.
const COLUMN_DEFS = {
    [COLUMN_IDS.RANK]: {
        head: () => (
            <TableCell sx={{ width: '2.5rem' }}>#</TableCell>
        ),
        body: (c) => <TableCell>{c.rank}</TableCell>,
    },
    [COLUMN_IDS.SCORE]: {
        head: (emphasized) => (
            <TableCell
                align="right"
                sx={emphasized ? { fontWeight: 'bold' } : undefined}
            >
                Score
            </TableCell>
        ),
        body: (c) => (
            <TableCell align="right">{formatScore(c.primaryScore)}</TableCell>
        ),
    },
    [COLUMN_IDS.FLAGS]: {
        head: (emphasized) => (
            <TableCell
                align="center"
                sx={{ width: '2rem', fontWeight: emphasized ? 'bold' : 'inherit' }}
            >
                <Tooltip title="Structural alert severity">
                    <span>⚠</span>
                </Tooltip>
            </TableCell>
        ),
        body: (c) => {
            const glyph = flagGlyph(c.flags);
            const flagCount = Array.isArray(c.flags) ? c.flags.length : 0;
            return (
                <TableCell align="center">
                    <Tooltip
                        title={
                            glyph === GLYPH_UNAVAILABLE
                                ? 'Alert scanning unavailable'
                                : `${flagCount} alert${flagCount === 1 ? '' : 's'}`
                        }
                    >
                        <span>{glyph}</span>
                    </Tooltip>
                </TableCell>
            );
        },
    },
    [COLUMN_IDS.INTERACTIONS]: {
        head: (emphasized) => (
            <TableCell
                align="right"
                sx={{ width: '3rem', fontWeight: emphasized ? 'bold' : 'inherit' }}
            >
                <Tooltip title="Protein–ligand interaction count">
                    <span>Int.</span>
                </Tooltip>
            </TableCell>
        ),
        body: (c) => <TableCell align="right">{c.interactionCount ?? 0}</TableCell>,
    },
};

const DEFAULT_COLUMN_ORDER = [
    COLUMN_IDS.RANK, COLUMN_IDS.SCORE,
    COLUMN_IDS.FLAGS, COLUMN_IDS.INTERACTIONS,
];

export default function ResultsTable({
    ranked, selectedId, onSelect,
    columnOrder = DEFAULT_COLUMN_ORDER,
    primaryEmphasis = null,
}) {
    if (!Array.isArray(ranked) || ranked.length === 0) return null;

    // Defensive — strip any unknown column ids the caller passed.
    const resolvedOrder = (columnOrder || DEFAULT_COLUMN_ORDER)
        .filter((id) => COLUMN_DEFS[id]);

    return (
        <Table size="small" sx={{ '& td, & th': { px: 0.75, py: 0.5 } }}>
            <TableHead>
                <TableRow>
                    {resolvedOrder.map((colId) => {
                        const def = COLUMN_DEFS[colId];
                        const emphasized = colId === primaryEmphasis;
                        return (
                            <span key={`h-${colId}`} style={{ display: 'contents' }}>
                                {def.head(emphasized)}
                            </span>
                        );
                    })}
                </TableRow>
            </TableHead>
            <TableBody>
                {ranked.map((c) => {
                    const isSelected = c.compoundId === selectedId;
                    return (
                        <TableRow
                            key={c.compoundId}
                            hover
                            selected={isSelected}
                            onClick={() => onSelect && onSelect(c.compoundId)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {resolvedOrder.map((colId) => {
                                const def = COLUMN_DEFS[colId];
                                return (
                                    <span
                                        key={`b-${c.compoundId}-${colId}`}
                                        style={{ display: 'contents' }}
                                    >
                                        {def.body(c)}
                                    </span>
                                );
                            })}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}
